import { useTranslate } from '@refinedev/core';
import { Card, Descriptions } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ContractRatesTable } from '@/components/tenancy/ContractRatesTable';
import { formatDate, formatDateTime } from '@/utils/format';
import type { ContractOut } from '../constants';

interface ContractGeneralTabProps {
  record: ContractOut;
  organization?: OrganizationOut;
  publisher?: PublisherListOut;
}

export const ContractGeneralTab = ({
  record,
  organization,
  publisher,
}: ContractGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(record.id),
    },
    {
      key: 'organization',
      label: t('tenancy.contracts.fields.organization'),
      children: (
        <ShowIconLink
          resource="tenancy.organizations"
          id={record.organization_id}
        >
          {organization?.name ?? record.organization_id}
        </ShowIconLink>
      ),
    },
    {
      key: 'publisher',
      label: t('tenancy.contracts.fields.publisher'),
      children:
        record.publisher_id != null ? (
          <ShowIconLink
            resource="user_catalog.publishers"
            id={record.publisher_id}
          >
            {publisher?.ancestry_with_name ?? record.publisher_id}
          </ShowIconLink>
        ) : (
          t('tenancy.contracts.publisher_default')
        ),
    },
    {
      key: 'valid_from',
      label: t('tenancy.contracts.fields.valid_from'),
      children: orDash(formatDate(record.valid_from)),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={items}
        />
      </Card>

      <Card
        title={t('tenancy.contracts.fields.rates')}
        style={{ marginTop: 16 }}
      >
        <ContractRatesTable rates={record.rates} />
      </Card>
    </>
  );
};
